import React, { useMemo } from 'react';

import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Alert, Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';

import type {
  ChainWeightingMode,
  ParityChainCompetitorAggregate,
  ParityChainCompetitorPoint,
} from '~/types/services/priceParrot';

import { PARITY_SECTION_BLOCK_MB, SECTION_SX } from '../PriceParrotParityTool/constants';
import { fmtShortDateWithYear } from '../PriceParrotParityTool/utils';

import DateRangeRow from './shared/DateRangeRow';
import FilterPillRow, { PillOption } from './shared/FilterPillRow';
import KpiTile from './shared/KpiTile';
import { fmtPct, fmtShortDate } from './utils';

interface Props {
  chainName: string | null;
  daily: Array<ParityChainCompetitorPoint>;
  /** Curated major-competitor buckets for this chain (drives pill list + ordering). */
  majorCompetitors: Array<string>;
  /** Range-aggregated headline numbers, one entry per major-competitor. */
  aggregates: Array<ParityChainCompetitorAggregate>;
  selectedCompetitor: string | null;
  onCompetitorChange: (next: string | null) => void;
  weighting: ChainWeightingMode;
  dateFrom: string;
  dateTo: string;
  minDate: string;
  maxDate: string;
  onDateFromChange: (next: string) => void;
  onDateToChange: (next: string) => void;
  onResetRange: () => void;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
}

export default function CompetitorChart({
  chainName,
  daily,
  majorCompetitors,
  aggregates,
  selectedCompetitor,
  onCompetitorChange,
  weighting,
  dateFrom,
  dateTo,
  minDate,
  maxDate,
  onDateFromChange,
  onDateToChange,
  onResetRange,
  isLoading,
  isError,
  errorMessage,
}: Props) {
  // Pills are driven by `summary.major_competitors` — the curated bucket list svc-vendor
  // sources from `parity_chain_competitor_daily.major_competitor`. Order follows that array.
  const competitorOptions = useMemo<Array<PillOption<string | null>>>(
    () =>
      majorCompetitors.map((name) => ({
        key: name,
        label: name,
        value: name,
      })),
    [majorCompetitors],
  );

  const aggregateForSelected = useMemo<ParityChainCompetitorAggregate | null>(() => {
    if (!selectedCompetitor) {
      return null;
    }
    return aggregates.find((a) => a.major_competitor === selectedCompetitor) ?? null;
  }, [aggregates, selectedCompetitor]);

  const rows = useMemo(() => {
    if (!selectedCompetitor) {
      return [];
    }
    return daily
      .filter((p) => p.major_competitor === selectedCompetitor)
      .map((p) => ({ report_date: p.report_date, value: p.avg_premium_when_le_overpriced }))
      .sort((a, b) => a.report_date.localeCompare(b.report_date));
  }, [daily, selectedCompetitor]);

  const rangeLabel = dateFrom && dateTo ? `${fmtShortDateWithYear(dateFrom)} – ${fmtShortDateWithYear(dateTo)}` : '—';

  const subHeaderLeft = [chainName, selectedCompetitor, 'average premium'].filter(Boolean).join(' · ');

  return (
    <Paper elevation={0} sx={SECTION_SX}>
      <Box sx={{ mb: PARITY_SECTION_BLOCK_MB }}>
        <Typography sx={{ fontSize: 24, fontWeight: 600 }}>Competitors — average premium (%)</Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5, maxWidth: 720 }}>
          Average premium when LE&apos;s rate is above the selected competitor&apos;s best rate. Pick a competitor to
          scope the chart. The headline number is weighted by overpriced comparisons in the selected range.
        </Typography>
      </Box>

      <Stack
        sx={{ mb: PARITY_SECTION_BLOCK_MB }}
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems="flex-start"
      >
        <FilterPillRow<string | null>
          label="Competitor"
          options={competitorOptions}
          value={selectedCompetitor}
          onChange={(next) => onCompetitorChange(next)}
        />

        <KpiTile
          label="Average premium"
          value={fmtPct(aggregateForSelected?.avg_premium_when_le_overpriced, 1)}
          caption={
            selectedCompetitor
              ? `${selectedCompetitor} · ${weighting}-weighted · ${
                  aggregateForSelected?.comparisons_le_overpriced?.toLocaleString() ?? '—'
                } comparisons`
              : 'Pick a competitor'
          }
          isLoading={isLoading}
        />
      </Stack>

      <Box sx={{ mb: PARITY_SECTION_BLOCK_MB }}>
        <DateRangeRow
          dateFrom={dateFrom}
          dateTo={dateTo}
          minDate={minDate}
          maxDate={maxDate}
          onDateFromChange={onDateFromChange}
          onDateToChange={onDateToChange}
          onReset={onResetRange}
        />
      </Box>

      {isError && errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2, position: 'relative', minHeight: 400 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{subHeaderLeft || '—'}</Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Range: {rangeLabel}</Typography>
        </Stack>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}
        {!isLoading && !isError && competitorOptions.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4 }}>
            No competitor data for this chain in the selected range.
          </Typography>
        )}
        {!isLoading && competitorOptions.length > 0 && rows.length === 0 && selectedCompetitor && (
          <Typography color="text.secondary" sx={{ py: 4 }}>
            No data points for {selectedCompetitor} in this range.
          </Typography>
        )}
        {!isLoading && rows.length > 0 && (
          <ResponsiveContainer width="100%" height={340}>
            <AreaChart data={rows} margin={{ top: 8, right: 16, bottom: 24, left: 0 }}>
              <defs>
                <linearGradient id="competitorPremiumFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f97316" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#f97316" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="report_date"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                tickFormatter={(v) => fmtShortDate(String(v))}
                label={{ value: 'Reporting period', position: 'insideBottom', offset: -12, fontSize: 12 }}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `${(Number(v) * 100).toFixed(1)}%`}
                label={{ value: 'Average premium (%)', angle: -90, position: 'insideLeft', fontSize: 12 }}
              />
              <Tooltip
                labelFormatter={(label) => fmtShortDate(String(label))}
                formatter={(value: number | string) => (value == null || value === '' ? '—' : fmtPct(Number(value)))}
              />
              <Area
                type="monotone"
                dataKey="value"
                name="Average premium"
                stroke="#f97316"
                strokeWidth={2}
                fill="url(#competitorPremiumFill)"
                connectNulls={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Paper>

      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.5 }}>
        Configurable range updates the chart. Headline number reflects comparisons in the selected window only.
      </Typography>
    </Paper>
  );
}
